import styled from '@emotion/styled';
import { Container, Flex, Hide } from '@qga/roo-ui/components';

import useBreakpoints, { isScreen } from '@shared/v2/hooks/useBreakpoints';

import { useHomepageHero } from '@/v2/hooks/featureFlags/useHomepageHero';
import {
  PrimaryDefault,
  PrimaryPromo,
  SecondaryPromo,
} from '@/v2/services/sanity/types/api/response/homepageHero';
import MobileHero from './components/MobileHero';
import PrimaryHero from './components/PrimaryHero';
import SecondaryHero from './components/SecondaryHero';

interface HeroProps {
  primaryPromo: PrimaryPromo;
  primaryDefault: PrimaryDefault;
  secondaryPromo: SecondaryPromo;
}

const Wrapper = styled.div`
  // This is to prevent layout shifting on mobile; Carousel is not SSR
  min-height: 330px;
  padding: 0;
  margin: 0;
  width: 100%;
`;

const DesktopContainer = styled(Container)`
  height: 100%;
`;

const DesktopHide = styled(Hide)`
  height: 100%;
`;

const Hero = ({ primaryPromo, primaryDefault, secondaryPromo }: HeroProps) => {
  const { showPrimaryAndSecondary } = useHomepageHero();
  const breakpoints = useBreakpoints();
  const isMobile = isScreen(breakpoints)('xxs', 'xs', 'sm');

  const MobileView = (
    <MobileHero
      primaryDefault={primaryDefault}
      primaryPromo={primaryPromo}
      secondaryPromo={secondaryPromo}
    />
  );

  const DesktopView = (
    <DesktopContainer px={0} data-testid="HOMEPAGE_HERO_DESKTOP">
      {/*
        Hiding this on mobile with media queries, to prevent flicker of carousel items
        being rendered due to isScreen hook returning undefined on first render.
      */}
      <DesktopHide xs sm>
        <Flex width="100%" height="100%" flexDirection="row" gap={[0, 0, 6]}>
          <PrimaryHero
            key="primary-hero"
            primaryPromo={primaryPromo}
            primaryDefault={primaryDefault}
            data-testid="HOMEPAGE_HERO_DESKTOP_PRIMARY"
          />
          {showPrimaryAndSecondary && (
            <SecondaryHero
              key="secondary-hero"
              {...secondaryPromo}
              data-testid="HOMEPAGE_HERO_DESKTOP_SECONDARY"
            />
          )}
        </Flex>
      </DesktopHide>
    </DesktopContainer>
  );

  return <Wrapper>{isMobile ? MobileView : DesktopView}</Wrapper>;
};

export default Hero;
